import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ShieldAlert, Search, Loader2 } from 'lucide-react'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { routes } from '@/lib/routes'
import { useAuth } from '@/contexts/auth-context'
import { useI18n } from '@/i18n'
import {useServices} from '@/services/context'
import type { Page } from '@/types/page'

interface UserWithRoles {
  id: string
  name: string
  email: string
  active: boolean
  roles: { id: string; name: string }[]
}

interface SwitchUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SwitchUserDialog({ open, onOpenChange }: SwitchUserDialogProps) {
  const services = useServices()
  const i18n = useI18n()
  const { user, startImpersonation } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [users, setUsers] = useState<UserWithRoles[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [confirmUser, setConfirmUser] = useState<UserWithRoles | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchUsers = useCallback((q: string) => {
    setLoading(true)
    services.users.list({size: 50, ...(q ? {q} : {})})
      .then((data) => setUsers((data as Page<UserWithRoles>).content))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [services.users])

  useEffect(() => {
    if (open) {
      setSearch('')
      fetchUsers('')
    }
  }, [open, fetchUsers])

  function handleSearchChange(value: string) {
    setSearch(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchUsers(value), 300)
  }

  const filteredUsers = users.filter(u => u.id !== user?.id)

  const handleConfirm = async () => {
    if (!confirmUser) return
    setSwitching(true)
    try {
      await startImpersonation(confirmUser.id)
      queryClient.removeQueries()
      onOpenChange(false)
      setConfirmUser(null)
      navigate(routes.dashboard())
    } catch (error) {
      console.error('Failed to switch user:', error)
    } finally {
      setSwitching(false)
    }
  }

  return (
    <>
      <Dialog open={open && !confirmUser} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" />
              {i18n.impersonation.switchUser}
            </DialogTitle>
            <DialogDescription>
              {i18n.impersonation.switchUserDialogDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={i18n.impersonation.searchPlaceholder}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{i18n.impersonation.noUsersFound}</p>
            ) : (
              filteredUsers.map(u => (
                <button
                  key={u.id}
                  onClick={() => setConfirmUser(u)}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-accent transition-colors"
                  data-testid={`btn-select-user-${u.id}`}
                >
                  <div className="font-medium text-sm" data-testid={`user-name-${u.id}`}>{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                  <div className="flex gap-1 mt-1">
                    {u.roles.map(r => (
                      <Badge key={r.id} variant="secondary" className="text-xs">
                        {r.name}
                      </Badge>
                    ))}
                  </div>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmUser} onOpenChange={(open) => { if (!open) setConfirmUser(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmUser ? i18n.impersonation.switchToUser({ name: confirmUser.name }) : ''}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmUser ? i18n.impersonation.switchConfirmDescription({ name: confirmUser.name, email: confirmUser.email }) : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={switching}>{i18n.common.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} disabled={switching}>
              {switching ? <ButtonSpinner/> : null}
              {i18n.impersonation.switchUser}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
